import { localDb } from './database'
import { queueLocalEntity } from './mutations'
import { ensureLocalIdentity } from '@/services/identity/local-identity'
import type { Note, PracticeRecord } from '@/domain/types'

type Session = { id: string; module: string; startedAtMs: number }
const metadataKey = (workspace: string, device: string) => `notebook-visit:${workspace}:${device}`
const base = (id: string, workspaceId: string, userId: string, now: number) => ({ id, workspaceId, ownerUserId: userId, createdAtMs: now, updatedAtMs: now, deletedAtMs: null, version: 1 })
const hash = async (value: string) => [...new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value)))].map(n => n.toString(16).padStart(2, '0')).join('').slice(0, 32)
const finalizing = new Map<string, Promise<void>>()
export async function finishNotebookVisit(session: Session) {
  const identity = await ensureLocalIdentity(), key = `${identity.workspaceId}:${session.id}`
  const pending = finalizing.get(key)
  if (pending) return pending
  const task = finalizeVisit(session).finally(() => finalizing.delete(key))
  finalizing.set(key, task)
  return task
}
async function finalizeVisit(session: Session) {
  const identity = await ensureLocalIdentity()
  const attempts = (await localDb.notes.where('[workspaceId+notebookModule]').equals([identity.workspaceId, '__question_practice__']).toArray())
    .filter(n => !n.deletedAtMs && n.questionAttempt?.notebookSessionId === session.id).map(n => n.questionAttempt!)
  const groups = new Map<string, typeof attempts>()
  for (const attempt of attempts) { const module = attempt.module || session.module || '综合'; groups.set(module, [...groups.get(module) || [], attempt]) }
  for (const [module, records] of groups) {
    const id = `nbpractice_${await hash(`${identity.workspaceId}:${session.id}:${module}`)}`
    if (await localDb.practiceRecords.get(id)) continue // One immutable summary per visit/module, including retries.
    const last = Math.max(...records.map(r => r.attemptedAtMs))
    const entity: PracticeRecord = { ...base(id, identity.workspaceId, identity.userId, session.startedAtMs),
      updatedAtMs: last, notebookSessionId: session.id, module, totalCount: records.length,
      correctCount: records.filter(r => r.correct).length, durationMinutes: 0,
      note: `错题笔记本 · 本次作答 ${records.length} 次（打开至关闭）`, source: 'manual' }
    await queueLocalEntity(localDb.practiceRecords, 'practice_record', entity, 'create', null)
  }
}
export async function notebookVisit(action: 'start' | 'end', session: Session) {
  const identity = await ensureLocalIdentity(), key = metadataKey(identity.workspaceId, identity.deviceId)
  const previous = (await localDb.appMetadata.get(key))?.value as Session | undefined
  if (action === 'start') {
    // A process kill may skip pagehide. Recover the prior visit from durable attempt events.
    if (previous && previous.id !== session.id) await finishNotebookVisit(previous)
    await localDb.appMetadata.put({ key, value: session, updatedAtMs: Date.now() })
  } else {
    await finishNotebookVisit(session)
    if (previous?.id === session.id) await localDb.appMetadata.delete(key)
  }
}
export async function notebookProgress(module: string, patch?: Note['notebookProgress']) {
  const identity = await ensureLocalIdentity(), scope = `__progress__:${module}`
  const all = (await localDb.notes.where('[workspaceId+notebookModule]').equals([identity.workspaceId, scope]).toArray())
    .filter(n => !n.deletedAtMs && n.type === 'mistake_notebook' && n.notebookModule === scope)
  if (!patch) {
    const sorted = all.sort((a, b) => b.updatedAtMs - a.updatedAtMs)
    const resetAt: Record<string, number> = {}
    for (const note of sorted) for (const [id, at] of Object.entries(note.notebookProgress?.resetAt || {})) resetAt[id] = Math.max(resetAt[id] || 0, at)
    return { lastQuestionId: sorted[0]?.notebookProgress?.lastQuestionId, resetAt }
  }
  const id = `nbprogress_${await hash(`${identity.workspaceId}:${identity.deviceId}:${module}`)}`
  const current = all.find(n => n.id === id), now = Date.now()
  const entity: Note = { ...(current || { ...base(id, identity.workspaceId, identity.userId, now), type: 'mistake_notebook', title: '错题本进度', content: '', category: '__mistake_notebook__', tags: [], source: '', reviewStage: 0, reviewCount: 0, nextReviewAtMs: null, lastReviewedAtMs: null }),
    updatedAtMs: now, notebookModule: scope,
    notebookProgress: { ...current?.notebookProgress, ...patch, resetAt: { ...current?.notebookProgress?.resetAt, ...patch.resetAt } } }
  await queueLocalEntity(localDb.notes, 'note', entity, current ? 'update' : 'create', current?.version ?? null)
  return entity.notebookProgress
}
